import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, RotateCcw } from "lucide-react";

import { toastError, presentMutationError } from "../../../../utils/showApiErrorToast";
import {
  BROKER_ALERT_TITLE_CLASS,
  BROKER_FIELD_DESC_CLASS,
} from "@/components/Broker/brokerAlertUi";
import { FieldSelectFit } from "@/components/trade-filter-selects";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useActiveBrokerAccount } from "../../../../hooks/useActiveBrokerAccount";
import { formatTradeAccountPrimaryLabel } from "../../../../services/liquidBrokersApi";
import {
  createPnlMilestone,
  deletePnlMilestone,
  fetchPnlMilestones,
  patchPnlMilestone,
  resetPnlBaseline,
  suppressAllAutoPnlMilestones,
} from "../../../../services/milestonesApi";
import type {
  CreatePnlMilestoneRequest,
  PnlMilestoneDefinition,
  PnlMilestoneDirection,
  PnlMilestoneScope,
  PnlMilestoneType,
  PnlMilestonesListResponse,
} from "../../../../types/milestones";
import { useDisplayCurrency } from "../../../../contexts/DisplayCurrencyContext";
import { formatDisplayCurrency } from "../../../../utils/formatDisplayCurrency";
import { toast } from "sonner";
import { accountSettingsInsetSurfaceClassName } from "../accountSettingsCardShell";
import SettingCard, {
  accountSettingsCardStackClassName,
  accountSettingsFieldTightControlClassName,
} from "./SettingCard";
import { PnlMilestoneCreateDialog } from "./PnlMilestoneCreateDialog";
import { PnlMilestoneDefinitionRow } from "./PnlMilestoneDefinitionRow";
import { formatMilestonePercent, isMilestoneReached } from "./pnlMilestonesUi";

function defaultCreatePayload(account_code: string): CreatePnlMilestoneRequest {
  return {
    account_code,
    is_active: true,
    scope: "delta",
    milestone_type: "absolute",
    direction: "up",
    threshold_value: 100,
    name: "",
  };
}

const PnlMilestonesSettings: React.FC = () => {
  const { displayCurrency } = useDisplayCurrency();
  const formatMoney = useCallback(
    (value: number) => formatDisplayCurrency(value, displayCurrency),
    [displayCurrency]
  );
  const { accounts, activeAccountCode, loading: accountsLoading } = useActiveBrokerAccount();
  const accountOptions = useMemo(
    () =>
      accounts.map((account) => ({
        value: account.accountCode,
        label: formatTradeAccountPrimaryLabel(account),
      })),
    [accounts]
  );

  const [selectedAccountCode, setSelectedAccountCode] = useState<string | null>(
    activeAccountCode ?? null
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState<PnlMilestonesListResponse | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Partial<PnlMilestoneDefinition>>>({});
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createDraft, setCreateDraft] = useState<CreatePnlMilestoneRequest | null>(null);

  const autoRows = useMemo(
    () => (data?.definitions ?? []).filter((definition) => Boolean(definition.is_auto ?? definition.is_system)),
    [data]
  );
  const activeMilestones = useMemo(
    () => (data?.definitions ?? []).filter((definition) => !isMilestoneReached(definition)),
    [data]
  );
  const achievedMilestones = useMemo(
    () => (data?.definitions ?? []).filter((definition) => isMilestoneReached(definition)),
    [data]
  );
  const allAutoDisabled =
    autoRows.length > 0 && autoRows.every((definition) => Boolean(definition.is_suppressed));

  useEffect(() => {
    if (!selectedAccountCode && activeAccountCode) {
      setSelectedAccountCode(activeAccountCode);
    }
  }, [activeAccountCode, selectedAccountCode]);

  const isPageLoading = accountsLoading;
  const milestonesLoading = Boolean(
    selectedAccountCode && (loading || data?.account_code !== selectedAccountCode)
  );

  const load = useCallback(async (accountCode: string, options?: { quiet?: boolean }) => {
    const quiet = options?.quiet === true;
    if (!quiet) setLoading(true);
    try {
      const response = await fetchPnlMilestones(accountCode);
      setData(response);
    } catch (error) {
      console.error(error);
      presentMutationError(error, "Failed to load milestones");
      if (!quiet) setData(null);
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  const handleBulkDisableAllAuto = useCallback(
    async (disable: boolean) => {
      if (!data?.account_code) return;
      setSaving(true);
      try {
        await suppressAllAutoPnlMilestones(data.account_code, disable);
        await load(data.account_code);
        toast.success(disable ? "All AUTO milestones disabled" : "All AUTO milestones re-enabled");
      } catch (error) {
        presentMutationError(error, "Failed to update AUTO milestones");
      } finally {
        setSaving(false);
      }
    },
    [data, load]
  );

  useEffect(() => {
    if (!selectedAccountCode) {
      setData(null);
      if (!accountsLoading && accounts.length === 0) {
        setLoading(false);
      }
      return;
    }
    void load(selectedAccountCode);
  }, [selectedAccountCode, load, accountsLoading, accounts.length]);

  const beginEdit = (milestone: PnlMilestoneDefinition) => {
    setEditingId(milestone.id);
    setDrafts((previous) => ({
      ...previous,
      [milestone.id]: { ...milestone },
    }));
  };

  const cancelEdit = (id: string) => {
    setEditingId((previous) => (previous === id ? null : previous));
    setDrafts((previous) => {
      const next = { ...previous };
      delete next[id];
      return next;
    });
  };

  const saveEdit = async (id: string) => {
    if (!data) return;
    const accountCode = data.account_code;
    const draft = drafts[id];
    if (!draft) return;

    setSaving(true);
    try {
      const patch = {
        name: draft.name ?? undefined,
        scope: draft.scope as PnlMilestoneScope | undefined,
        milestone_type: draft.milestone_type as PnlMilestoneType | undefined,
        direction: draft.direction as PnlMilestoneDirection | undefined,
        threshold_value:
          typeof draft.threshold_value === "number" ? draft.threshold_value : undefined,
      };
      const updated = await patchPnlMilestone(id, accountCode, patch);
      setData({
        ...data,
        definitions: data.definitions.map((definition) =>
          definition.id === id ? updated : definition
        ),
      });
      cancelEdit(id);
      toast.success("Milestone updated");
    } catch (error) {
      presentMutationError(error, "Failed to update milestone");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!data) return;
    setSaving(true);
    try {
      await deletePnlMilestone(id, data.account_code);
      setData({
        ...data,
        definitions: data.definitions.filter((definition) => definition.id !== id),
      });
      toast.success("Milestone deleted");
    } catch (error) {
      presentMutationError(error, "Failed to delete milestone");
    } finally {
      setSaving(false);
    }
  };

  const handleResetBaseline = async () => {
    if (!data) return;
    setSaving(true);
    try {
      await resetPnlBaseline(data.account_code);
      await load(data.account_code);
      toast.success("Baseline reset");
    } catch (error) {
      presentMutationError(error, "Failed to reset baseline");
    } finally {
      setSaving(false);
    }
  };

  const openCreate = () => {
    if (!selectedAccountCode) return;
    setCreateDraft(defaultCreatePayload(selectedAccountCode));
    setIsCreateOpen(true);
  };

  const submitCreate = async () => {
    if (!createDraft) return;
    setSaving(true);
    try {
      const created = await createPnlMilestone(createDraft);
      if (data && data.account_code === created.account_code) {
        setData({ ...data, definitions: [created, ...data.definitions] });
      } else if (selectedAccountCode) {
        await load(selectedAccountCode);
      }
      toast.success("Milestone created");
      setIsCreateOpen(false);
      setCreateDraft(null);
    } catch (error) {
      presentMutationError(error, "Failed to create milestone");
    } finally {
      setSaving(false);
    }
  };

  const renderMilestoneList = (
    milestones: PnlMilestoneDefinition[],
    emptyMessage: string
  ): React.ReactNode => {
    if (milestones.length === 0) {
      return <FieldDescription className={BROKER_FIELD_DESC_CLASS}>{emptyMessage}</FieldDescription>;
    }

    return (
      <div className="flex flex-col gap-6">
        {milestones.map((milestone) => (
          <PnlMilestoneDefinitionRow
            key={milestone.id}
            milestone={milestone}
            isEditing={editingId === milestone.id}
            draft={drafts[milestone.id]}
            saving={saving}
            formatMoney={formatMoney}
            onDraftChange={(patch) =>
              setDrafts((previous) => ({
                ...previous,
                [milestone.id]: { ...previous[milestone.id], ...patch },
              }))
            }
            onBeginEdit={() => beginEdit(milestone)}
            onCancelEdit={() => cancelEdit(milestone.id)}
            onSaveEdit={() => void saveEdit(milestone.id)}
            onDelete={() => void handleDelete(milestone.id)}
            onReloadQuiet={(accountCode) => load(accountCode, { quiet: true })}
            setSaving={setSaving}
          />
        ))}
      </div>
    );
  };

  const renderMilestonesCardBody = (
    milestones: PnlMilestoneDefinition[],
    emptyMessage: string
  ): React.ReactNode => {
    if (!selectedAccountCode) {
      return (
        <FieldDescription className={BROKER_FIELD_DESC_CLASS}>
          Select an account to view milestones.
        </FieldDescription>
      );
    }
    if (milestonesLoading) {
      return (
        <div className="flex min-h-20 w-full items-center justify-center">
          <Loader2 className="size-5 animate-spin text-[color:var(--brand-tropical-surf)]" aria-hidden />
        </div>
      );
    }
    if (!data) {
      return (
        <FieldDescription className={BROKER_FIELD_DESC_CLASS}>
          Select an account to view milestones.
        </FieldDescription>
      );
    }
    return renderMilestoneList(milestones, emptyMessage);
  };

  if (isPageLoading) {
    return (
      <div className="flex min-h-60 w-full items-center justify-center">
        <Loader2 className="size-6 animate-spin text-[color:var(--brand-tropical-surf)]" aria-hidden />
      </div>
    );
  }

  return (
    <div
      className={cn(accountSettingsCardStackClassName, "min-w-0 overflow-x-hidden")}
      data-pnl-milestones-settings=""
      data-account-settings-card-stack=""
    >
      <SettingCard
        title="PnL milestones"
        description="View and manage PnL milestones per broker account: AUTO rows from the system, plus your own definitions."
      >
        <div
          className="grid w-full min-w-0 grid-cols-1 items-end gap-4 md:grid-cols-[minmax(0,auto)_minmax(0,1fr)] md:gap-6"
          data-pnl-milestones-account-toolbar=""
        >
          <Field
            className={cn(accountSettingsFieldTightControlClassName, "w-fit")}
            data-pnl-milestones-account-field=""
          >
            <FieldLabel htmlFor="pnl-milestones-account" className={BROKER_ALERT_TITLE_CLASS}>
              Account
            </FieldLabel>
            <FieldSelectFit
              id="pnl-milestones-account"
              value={selectedAccountCode ?? ""}
              onValueChange={(value) => setSelectedAccountCode(value || null)}
              options={
                accountOptions.length > 0
                  ? accountOptions
                  : [{ value: "", label: "No accounts", disabled: true }]
              }
              placeholder="Select account"
              emptyValueShowsPlaceholder
              disabled={accountOptions.length === 0}
            />
          </Field>

          <div
            className="flex flex-wrap items-center justify-end gap-3"
            data-pnl-milestones-account-actions=""
          >
            {!milestonesLoading && autoRows.length > 0 ? (
              <div className="flex shrink-0 items-center gap-2">
                <Switch
                  id="pnl-milestones-disable-all-auto"
                  checked={allAutoDisabled}
                  disabled={saving || milestonesLoading}
                  onCheckedChange={(checked) => void handleBulkDisableAllAuto(checked === true)}
                />
                <FieldLabel
                  htmlFor="pnl-milestones-disable-all-auto"
                  className={`${BROKER_FIELD_DESC_CLASS} cursor-pointer font-normal leading-none`}
                >
                  Disable all AUTO
                </FieldLabel>
              </div>
            ) : null}

            <Button
              type="button"
              variant="outline-gray"
              size="sm"
              data-font="primary"
              className="shrink-0"
              disabled={!data || saving || milestonesLoading}
              aria-busy={saving || undefined}
              onClick={() => void handleResetBaseline()}
            >
              {saving ? (
                <Loader2 data-icon="inline-start" className="size-3.5 animate-spin" aria-hidden />
              ) : (
                <RotateCcw data-icon="inline-start" className="size-3.5" aria-hidden />
              )}
              Reset baseline
            </Button>
            <Button
              type="button"
              variant="gradient"
              size="sm"
              data-font="primary"
              className="shrink-0"
              disabled={!selectedAccountCode || saving || milestonesLoading}
              onClick={openCreate}
            >
              Add milestone
            </Button>
          </div>
        </div>
      </SettingCard>

      <SettingCard
        title="Baseline"
        description="Percent milestones depend on baseline-generated PnL."
      >
        {!selectedAccountCode ? (
          <FieldDescription className={BROKER_FIELD_DESC_CLASS}>
            Select an account to view milestones.
          </FieldDescription>
        ) : milestonesLoading ? (
          <div className="flex min-h-20 w-full items-center justify-center">
            <Loader2 className="size-5 animate-spin text-[color:var(--brand-tropical-surf)]" aria-hidden />
          </div>
        ) : !data ? (
          <FieldDescription className={BROKER_FIELD_DESC_CLASS}>
            Select an account to view milestones.
          </FieldDescription>
        ) : (
          <div data-shadcn-data-table="" data-pnl-milestones-baseline-table="" className={cn("w-full min-w-0", accountSettingsInsetSurfaceClassName)}>
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="min-w-[12rem]">Metric</TableHead>
                  <TableHead data-column-align="end">Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[
                  [
                    "Current generated PnL",
                    data.current_generated_pnl == null ? "—" : formatMoney(data.current_generated_pnl),
                  ],
                  [
                    "Delta generated PnL",
                    data.delta_generated_pnl == null ? "—" : formatMoney(data.delta_generated_pnl),
                  ],
                  [
                    "Current return %",
                    data.current_return_pct == null ? "—" : formatMilestonePercent(data.current_return_pct),
                  ],
                  [
                    "Baseline generated PnL",
                    data.baseline_generated_pnl == null ? "—" : formatMoney(data.baseline_generated_pnl),
                  ],
                  [
                    "Baseline set at",
                    data.baseline_generated_pnl_at
                      ? new Date(data.baseline_generated_pnl_at).toLocaleString()
                      : "—",
                  ],
                ].map(([label, value]) => (
                  <TableRow key={label} className="hover:bg-transparent">
                    <TableCell className="whitespace-normal">{label}</TableCell>
                    <TableCell
                      data-column-align="end"
                      data-pnl-milestone-baseline-value=""
                      className="tabular-nums whitespace-normal"
                    >
                      {value}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </SettingCard>

      <SettingCard
        title="Active milestones"
        description="Milestones in progress or not yet started for the selected account."
      >
        <div data-pnl-milestones-active="">{renderMilestonesCardBody(activeMilestones, "No active milestones.")}</div>
      </SettingCard>

      <SettingCard
        title="Achieved milestones"
        description="Milestones you have already crossed for this account."
      >
        <div data-pnl-milestones-achieved="">
          {renderMilestonesCardBody(achievedMilestones, "No achieved milestones yet.")}
        </div>
      </SettingCard>

      <PnlMilestoneCreateDialog
        open={isCreateOpen}
        draft={createDraft}
        saving={saving}
        onOpenChange={(open) => {
          if (!open) {
            setIsCreateOpen(false);
            setCreateDraft(null);
          } else {
            setIsCreateOpen(true);
          }
        }}
        onDraftChange={setCreateDraft}
        onSubmit={() => void submitCreate()}
      />
    </div>
  );
};

export default PnlMilestonesSettings;
